import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import IconButton from '@atlaskit/button/icon/button';
import GenerativeIndicatorIcon from '@atlaskit/icon-lab/core/generative-indicator';

import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';

import { AnalyticContexts } from './analyticsUtils.tsx';

interface ReviewButtonProps {
	onStartThinking: () => void;
	isDisabled: boolean;
	// Renders a blue dot signalling unseen AI suggestions on this page.
	showNotificationDot?: boolean;
}

const containerStyles = css({
	display: 'inline-flex',
	position: 'relative',
});

const dotStyles = css({
	backgroundColor: token('color.icon.brand'),
	borderRadius: token('radius.full'),
	border: `${token('border.width.selected')} solid ${token('color.border.inverse')}`,
	display: 'block',
	height: token('space.075'),
	width: token('space.075'),
	pointerEvents: 'none',
	position: 'absolute',
	right: '3px',
	top: '3px',
});

const i18n = defineMessages({
	suggestionsLabel: {
		id: 'object-sidebar-components.suggestions.button.label',
		defaultMessage: 'Suggestions',
		description:
			'Tooltip and accessible label for the sparkle icon button in the object sidebar that opens AI suggestions in the editor',
	},
	unseenSuggestionsLabel: {
		id: 'object-sidebar-components.review.button.unseen-suggestions.label',
		defaultMessage: 'New suggestions available',
		description:
			'Accessible label for the blue notification dot shown on the Suggestions button when there are AI suggestions the user has not yet seen',
	},
});

const analyticsContext = {
	source: AnalyticContexts.Source,
	name: `${AnalyticContexts.Source}ReviewButton`,
	attributes: {
		source: AnalyticContexts.Source,
		name: `${AnalyticContexts.Source}ReviewButton`,
	},
};

export const ReviewButton = ({
	onStartThinking,
	isDisabled,
	showNotificationDot = false,
}: ReviewButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();

	return (
		<Tooltip position="left" content={<FormattedMessage {...i18n.suggestionsLabel} />} delay={0}>
			<span css={containerStyles}>
				<IconButton
					icon={GenerativeIndicatorIcon}
					appearance="subtle"
					label={formatMessage(i18n.suggestionsLabel)}
					testId="object-sidebar-review-button"
					shape={isRovoButtonEnabled ? 'default' : 'circle'}
					onClick={onStartThinking}
					isDisabled={isDisabled}
					analyticsContext={analyticsContext}
				/>
				{showNotificationDot && (
					<span
						css={dotStyles}
						data-testid="object-sidebar-review-button-notification-dot"
						role="status"
						aria-label={formatMessage(i18n.unseenSuggestionsLabel)}
					/>
				)}
			</span>
		</Tooltip>
	);
};
